import React, { useState, useEffect } from 'react';
import { adminAPI } from '../api/adminAPI';
import AdminLayout from '../components/AdminLayout';

const ChatbotResponsesPage = () => {
  const [responses, setResponses] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingResponse, setEditingResponse] = useState(null);

  const categories = ['greeting', 'services', 'pricing', 'support', 'general', 'faq', 'other'];
  const matchTypes = ['exact', 'contains', 'regex', 'fuzzy'];

  const [formData, setFormData] = useState({
    name: '',
    trigger: '',
    response: '',
    category: 'general',
    intent: '',
    followUpQuestions: [],
    buttons: [],
    priority: 50,
    isActive: true,
    matchType: 'contains'
  });

  const [configData, setConfigData] = useState({
    name: 'LionTek Assistant',
    welcomeMessage: 'Hello! How can I help you today?',
    isActive: true,
    quickReplies: []
  });

  useEffect(() => {
    fetchResponses();
    fetchConfig();
  }, []);

  const fetchResponses = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.get('/chatbot/responses');
      setResponses(response.data.responses || []);
    } catch (error) {
      console.error('Error fetching responses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await adminAPI.get('/chatbot/config');
      setConfig(response.data);
      setConfigData({
        name: response.data.name,
        welcomeMessage: response.data.welcomeMessage,
        isActive: response.data.isActive,
        quickReplies: response.data.quickReplies || []
      });
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingResponse) {
        await adminAPI.put(`/chatbot/responses/${editingResponse.id}`, formData);
      } else {
        await adminAPI.post('/chatbot/responses', formData);
      }
      fetchResponses();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving response:', error);
      alert('Failed to save response');
    }
  };

  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.put('/chatbot/config', configData);
      fetchConfig();
      setShowConfigModal(false);
      alert('Configuration updated successfully!');
    } catch (error) {
      console.error('Error updating config:', error);
      alert('Failed to update configuration');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this response?')) return;

    try {
      await adminAPI.delete(`/chatbot/responses/${id}`);
      fetchResponses();
    } catch (error) {
      console.error('Error deleting response:', error);
      alert('Failed to delete response');
    }
  };

  const handleEdit = (response) => {
    setEditingResponse(response);
    setFormData({
      name: response.name,
      trigger: response.trigger,
      response: response.response,
      category: response.category,
      intent: response.intent || '',
      followUpQuestions: response.followUpQuestions || [],
      buttons: response.buttons || [],
      priority: response.priority,
      isActive: response.isActive,
      matchType: response.matchType
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingResponse(null);
    setFormData({
      name: '',
      trigger: '',
      response: '',
      category: 'general',
      intent: '',
      followUpQuestions: [],
      buttons: [],
      priority: 50,
      isActive: true,
      matchType: 'contains'
    });
  };

  const addFollowUp = () => {
    const question = prompt('Enter follow-up question:');
    if (question) {
      setFormData({
        ...formData,
        followUpQuestions: [...formData.followUpQuestions, question]
      });
    }
  };

  const addButton = () => {
    const label = prompt('Enter button label:');
    if (!label) return;
    const url = prompt('Enter button URL:');
    if (!url) return;

    setFormData({
      ...formData,
      buttons: [...formData.buttons, { label, url }]
    });
  };

  const addQuickReply = () => {
    const reply = prompt('Enter quick reply text:');
    if (reply) {
      setConfigData({
        ...configData,
        quickReplies: [...configData.quickReplies, reply]
      });
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Chatbot Responses</h1>
          <p className="text-gray-600 mt-2">Manage canned responses and chatbot configuration</p>
        </div>

        {/* Actions */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setShowModal(true)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            + Add Response
          </button>
          <button
            onClick={() => setShowConfigModal(true)}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            ⚙️ Configure Chatbot
          </button>
        </div>

        {/* Responses List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {responses.map(response => (
              <div key={response.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-lg">{response.name}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        response.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {response.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {response.category}
                      </span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                        {response.matchType}
                      </span>
                      <span className="text-sm text-gray-500">Priority: {response.priority}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">Trigger:</span> {response.trigger}
                    </p>
                    <p className="text-sm text-gray-700 mb-2">{response.response}</p>
                    <p className="text-xs text-gray-500">Used {response.useCount} times</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(response)}
                      className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(response.id)}
                      className="px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Response Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl m-4 max-h-[90vh] flex flex-col">
              <div className="p-6 border-b flex-shrink-0">
                <h2 className="text-2xl font-bold">
                  {editingResponse ? 'Edit Response' : 'Add New Response'}
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                <div className="p-6 overflow-y-auto flex-1">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Category *</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat.toUpperCase()}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Match Type *</label>
                      <select
                        value={formData.matchType}
                        onChange={(e) => setFormData({ ...formData, matchType: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        {matchTypes.map(type => (
                          <option key={type} value={type}>{type.toUpperCase()}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Priority</label>
                      <input
                        type="number"
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Trigger *</label>
                    <input
                      type="text"
                      required
                      value={formData.trigger}
                      onChange={(e) => setFormData({ ...formData, trigger: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Keywords or pattern"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Response *</label>
                    <textarea
                      required
                      value={formData.response}
                      onChange={(e) => setFormData({ ...formData, response: e.target.value })}
                      rows="4"
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm">Active</span>
                    </label>
                  </div>
                </div>

                <div className="p-6 border-t flex gap-3 flex-shrink-0">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingResponse ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Config Modal */}
        {showConfigModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl m-4">
              <div className="p-6 border-b">
                <h2 className="text-2xl font-bold">Chatbot Configuration</h2>
              </div>

              <form onSubmit={handleConfigSubmit} className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Chatbot Name</label>
                  <input
                    type="text"
                    value={configData.name}
                    onChange={(e) => setConfigData({ ...configData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Welcome Message</label>
                  <textarea
                    value={configData.welcomeMessage}
                    onChange={(e) => setConfigData({ ...configData, welcomeMessage: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">Quick Replies</label>
                    <button
                      type="button"
                      onClick={addQuickReply}
                      className="text-sm text-blue-600"
                    >
                      + Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {configData.quickReplies.map((reply, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={reply}
                          onChange={(e) => {
                            const newReplies = [...configData.quickReplies];
                            newReplies[i] = e.target.value;
                            setConfigData({ ...configData, quickReplies: newReplies });
                          }}
                          className="flex-1 px-3 py-2 border rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setConfigData({
                              ...configData,
                              quickReplies: configData.quickReplies.filter((_, idx) => idx !== i)
                            });
                          }}
                          className="text-red-600"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={configData.isActive}
                      onChange={(e) => setConfigData({ ...configData, isActive: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm">Enable Chatbot</span>
                  </label>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowConfigModal(false)}
                    className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Save Configuration
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ChatbotResponsesPage;
